import isEmpty from 'lodash/isEmpty';

import {
  getColorOptions,
  getDataZoomOptions,
  getXAxisOptions,
  getXAxisOptionsForAudience,
  getYAxisOptions,
  getYAxisOptionsForAudience
} from './bar-graph-options';
import { getTooltip } from './bar-graph-tooltip';
import { abbrNum, getMetricValueInPercentage } from 'utils/helper/common';

import type {
  BarGraph,
  BarGraphDateInterval,
  BarGraphEndpointDataItem,
  BarGraphEndpointDataProp,
  BarGraphMinMax,
  BarGraphSeriesItem
} from '../types/BarGraph.type';

export const DEBOUNCE_DELAY = 250;
export const MAX_BAR_WIDTH = 50;

const ENGAGEMENT_RATE = 'engagementRate';

const getStyleMapping = (color: string): any => ({
  itemStyle: {
    color
  },

  emphasis: {
    itemStyle: {
      color
    }
  }
});

export const AUDIENCE_BY_AGE_STYLE_MAPPING = {
  '13-17': getStyleMapping('#f6ebfa'),
  '18-24': getStyleMapping('#e6c3f0'),
  '25-34': getStyleMapping('#d59ce7'),
  '35-44': getStyleMapping('#c475de'),
  '45-64': getStyleMapping('#ac3ad0')
};

export const getMinMax = (data: Array<number>): BarGraphMinMax => ({
  min: Math.min(...data),
  max: Math.max(...data)
});

export const getMaxBarWidth = (data: Array<BarGraphSeriesItem>): number | undefined | null => (data?.length < 3 ? MAX_BAR_WIDTH : null);

export const getSeriesData = (
  data: Array<BarGraphEndpointDataItem>,
  dataProp: BarGraphEndpointDataProp,
  channel: string
): Array<BarGraphSeriesItem> => data.map(dataItem => {
  const value = dataItem[dataProp] || 0;

  return {
    name: dataItem.date,
    value: dataProp === ENGAGEMENT_RATE ? value * 100 : value,
    tooltip: getTooltip(channel, dataProp, dataItem.url)
  };
});

export const getData = (
  data: Array<BarGraphEndpointDataItem>,
  dataProp: BarGraphEndpointDataProp,
  channel: string,
  dateInterval: BarGraphDateInterval
): Partial<BarGraph> => {
  if (isEmpty(data)) { // `isEmpty` also covers `null`
    return {};
  }

  const dates = data.map(item => item.date || '');
  const seriesData = getSeriesData(data, dataProp, channel);
  const xAxis = getXAxisOptions(dates, dateInterval);
  const yAxis = getYAxisOptions(dataProp);

  return {
    color: getColorOptions(),
    dataZoom: getDataZoomOptions(),
    grid: {
      top: 16
    },
    xAxis,
    yAxis,
    series: [{
      type: 'bar',
      barMaxWidth: getMaxBarWidth(seriesData),
      cursor: 'auto',
      data: seriesData
    }],
    tooltip: {
      trigger: 'item',
      enterable: true
    }
  };
};

export const getAudienceData = (seriesData: Array<BarGraphSeriesItem>, ageSplit: Array<string | number>): BarGraph => ({
  xAxis: getXAxisOptionsForAudience(ageSplit),
  yAxis: getYAxisOptionsForAudience(),

  grid: {
    left: 50,
    top: 40,
    bottom: 45
  },

  series: [{
    data: seriesData,
    type: 'bar',
    barWidth: '50%',
    barMaxWidth: null,
    cursor: 'auto',
    label: {
      show: true,
      position: 'top',
      formatter(value) {
        return [`{value|${abbrNum(value.value, { decimal: 1 }, true)}}`,
          `{percentage|(${getMetricValueInPercentage(value.data.percentage)})}`].join('\n');
      },
      rich: {
        value: {
          fontFamily: 'Hind',
          fontSize: 13,
          fontWeight: 600,
          align: 'center',
          color: '#3a3a3a',
          lineHeight: 22
        },
        percentage: {
          fontFamily: 'Hind',
          align: 'center',
          color: '#3a3a3a',
          fontSize: 12
        }
      },
      distance: 2
    }
  }]
});
